import { sql } from "drizzle-orm";
import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  real,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { branches } from "./branches";
import { productVariants } from "./catalog";
import { orders } from "./orders";

export type Unit = "g" | "kg" | "ml" | "l" | "pcs";

export const INVENTORY_STOCK_POLICY_ITEM_TYPES = [
  "INGREDIENT",
  "RETAIL_VARIANT",
] as const;

export type InventoryStockPolicyItemType =
  (typeof INVENTORY_STOCK_POLICY_ITEM_TYPES)[number];

export const ingredientCategories = pgTable(
  "ingredient_categories",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: text("name").notNull().unique(),
    code: text("code").unique(),
    sortOrder: integer("sort_order").notNull().default(0),
    isActive: boolean("is_active").notNull().default(true),
    description: text("description"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("ingredient_categories_active_sort_idx").on(t.isActive, t.sortOrder),
  ],
);

export const ingredients = pgTable(
  "ingredients",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: text("name").notNull(),
    baseUnit: text("base_unit").$type<Unit>().notNull(),
    receiveUnit: text("receive_unit").notNull(),
    receiveToBase: real("receive_to_base").notNull(),
    issueUnit: text("issue_unit").notNull(),
    issueToBase: real("issue_to_base").notNull(),
    categoryId: text("category_id").references(() => ingredientCategories.id, {
      onDelete: "set null",
    }),
    defaultSupplierId: text("default_supplier_id"),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("ingredients_active_idx").on(t.isActive),
    index("ingredients_category_idx").on(t.categoryId),
    index("ingredients_default_supplier_idx").on(t.defaultSupplierId),
  ],
);

export const recipes = pgTable(
  "recipes",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "cascade" }),
    yieldQty: real("yield_qty").notNull(),
    wastePct: real("waste_pct"),
    roundingRule: text("rounding_rule"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("recipes_variant_idx").on(t.variantId)],
);

export const recipeItems = pgTable(
  "recipe_items",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    recipeId: text("recipe_id")
      .notNull()
      .references(() => recipes.id, { onDelete: "cascade" }),
    ingredientId: text("ingredient_id")
      .notNull()
      .references(() => ingredients.id, { onDelete: "cascade" }),
    qty: real("qty").notNull(),
    unit: text("unit").notNull(),
    toBaseFactor: real("to_base_factor").notNull().default(1),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("recipe_items_recipe_idx").on(t.recipeId),
    index("recipe_items_ingredient_idx").on(t.ingredientId),
  ],
);

export const inventoryLots = pgTable(
  "inventory_lots",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "cascade" }),
    itemType: text("item_type", {
      enum: ["INGREDIENT", "RETAIL_VARIANT"],
    }).notNull(),
    itemId: text("item_id").notNull(),
    lotNo: text("lot_no").notNull(),
    expiryDate: date("expiry_date"),
    receivedAt: timestamp("received_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    qtyInBaseUnit: real("qty_in_base_unit").notNull(),
    qtyRemaining: real("qty_remaining").notNull(),
    costPerUnitLak: integer("cost_per_unit_lak"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("inventory_lots_branch_idx").on(t.branchId),
    index("inventory_lots_item_idx").on(t.itemType, t.itemId),
    index("inventory_lots_expiry_idx").on(t.expiryDate),
  ],
);

export const inventoryMovements = pgTable(
  "inventory_movements",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "cascade" }),
    lotId: text("lot_id")
      .notNull()
      .references(() => inventoryLots.id, { onDelete: "cascade" }),
    reason: text("reason", {
      enum: ["PURCHASE", "SALE", "WASTE", "ADJUST", "TRANSFER_OUT", "TRANSFER_IN"],
    }).notNull(),
    qtyDelta: real("qty_delta").notNull(),
    refOrderId: text("ref_order_id").references(() => orders.id, {
      onDelete: "set null",
    }),
    refTransferId: text("ref_transfer_id"),
    refPoId: text("ref_po_id"),
    refReceiptId: text("ref_receipt_id"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("inventory_movements_branch_idx").on(t.branchId),
    index("inventory_movements_lot_idx").on(t.lotId),
    index("inventory_movements_created_idx").on(t.createdAt),
    index("inventory_movements_ref_po_idx").on(t.refPoId),
    index("inventory_movements_ref_receipt_idx").on(t.refReceiptId),
  ],
);

export const inventoryStockPolicies = pgTable(
  "inventory_stock_policies",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    itemType: text("item_type", {
      enum: INVENTORY_STOCK_POLICY_ITEM_TYPES,
    }).notNull(),
    itemId: text("item_id").notNull(),
    branchId: text("branch_id").references(() => branches.id, {
      onDelete: "cascade",
    }),
    minQty: real("min_qty").notNull(),
    targetQty: real("target_qty"),
    isActive: boolean("is_active").notNull().default(true),
    note: text("note"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("inventory_stock_policies_branch_item_idx").on(
      t.branchId,
      t.itemType,
    ),
    index("inventory_stock_policies_item_idx").on(t.itemType, t.itemId),
    uniqueIndex("inventory_stock_policies_global_uniq")
      .on(t.itemType, t.itemId)
      .where(sql`${t.branchId} is null and ${t.isActive} = true`),
    uniqueIndex("inventory_stock_policies_branch_uniq")
      .on(t.itemType, t.itemId, t.branchId)
      .where(sql`${t.branchId} is not null and ${t.isActive} = true`),
  ],
);

export const stockSnapshots = pgTable(
  "stock_snapshots",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "cascade" }),
    itemType: text("item_type").notNull(),
    itemId: text("item_id").notNull(),
    qtyOnHand: real("qty_on_hand").notNull(),
    at: timestamp("at", { withTimezone: true, mode: "date" }).notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("stock_snapshots_branch_item_idx").on(
      t.branchId,
      t.itemType,
      t.itemId,
    ),
    index("stock_snapshots_at_idx").on(t.at),
  ],
);
